import { ApplicationController, observe, unobserve } from "@mixitone/mvc";
import { AuthEvent, type Session, type SessionAdapter } from "lib/session/SessionAdapter";

interface State {
  loginState: LoginState;
  session: Session | null;
}

type LoginState = "unknown" | "signing-up" | "signed-in" | "signed-out" | "password-recovery";

export default class SessionController extends ApplicationController<State> {
  adapter!: SessionAdapter;

  override get initialState(): State {
    return { loginState: "unknown", session: null };
  }

  override async initialize(props: never) {
    super.initialize(props);
    const adapter = window.sessionAdapter;
    if (!adapter) {
      throw new Error("Session adapter not found");
    }
    await adapter.initialize();
    this.adapter = adapter;

    this.adapter.onAuthStateChange(async (event, session) => {
      // Block updates while signing up
      if (this.signingUp) return;

      switch (event) {
        case AuthEvent.INITIAL_SESSION:
        case AuthEvent.SIGNED_IN:
        case AuthEvent.TOKEN_REFRESHED:
          this.actionSetSession(session);
          break;
        case AuthEvent.SIGNED_OUT:
          this.actionSetSession(null);
          break;
        case AuthEvent.PASSWORD_RECOVERY:
          this.actionBeginPasswordRecovery(session);
          break;
        case AuthEvent.USER_UPDATED:
          await this.actionSetSession(session);
          break;
      }
    });
  }

  async waitForSession(): Promise<State["loginState"]> {
    if (this.state.loginState !== "unknown") return this.state.loginState;

    return new Promise((resolve) => {
      const reaction = observe(() => {
        if (this.state.loginState !== "unknown") {
          unobserve(reaction);
          resolve(this.state.loginState);
        }
      });
    });
  }

  actionSigningUp() {
    this.state.loginState = "signing-up";
  }

  async actionCompleteSignUp(session: Session) {
    this.state.loginState = "unknown";
    return this.actionSetSession(session);
  }

  actionBeginPasswordRecovery(session: Session | null) {
    this.state.session = session;
    this.state.loginState = "password-recovery";
  }

  async actionSetSession(session: Session | null) {
    if (session && session === this.state.session) return;

    // If the session changed but the user did not and loading is true then just
    // set the session and return
    if (session?.user?.id === this.state.session?.user?.id && this.state.loginState === "signed-in") {
      this.debug("session changed but user did not");
      this.state.session = session;
      return;
    }

    this.state.session = session;

    if (session) {
      if (this.state.loginState === "signing-up") {
        // If the user is signing up then we don't need to load the data right now
        this.debug("user is signing up");
        return;
      }

      this.state.loginState = "signed-in";
    } else {
      this.debug("no session found");
      this.state.loginState = "signed-out";
    }
  }

  async actionSignOut() {
    console.log("signing out");
    await this.adapter.signOut();
    await this.actionSetSession(null);
    console.log("signed out", this.state.loginState);
  }

  get signedIn() {
    return this.state.loginState === "signed-in";
  }

  // We've specifically signed out
  get signedOut() {
    return this.state.loginState === "signed-out";
  }

  get signingUp() {
    return this.state.loginState === "signing-up";
  }

  get recoveringPassword() {
    return this.state.loginState === "password-recovery";
  }

  get isAnonymous() {
    return this.state.session?.user?.is_anonymous;
  }

  get user() {
    return this.state.session?.user;
  }
}
